import type { OthersEvent, User } from "@liveblocks/client"
import { isDefined } from "@prismicio/editor-support/Type"
import {
  AnimatedList,
  Avatar,
  Box,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Text,
  theme,
  Tooltip,
} from "@prismicio/editor-ui"
import type { ReactNode } from "react"

import { getAuthorDisplayName } from "../../documents/DocumentListBody/ActionBar/utils"

interface AvatarStackProps {
  /**
   * The amount each avatar overlaps the previous one.
   *
   * @default "-6"
   */
  overlapMargin?: "-6" | "-12"
  /**
   * The maximum amount of avatars to show.
   *
   * @default 4
   */
  maximumAmountOfShownAvatars?: number
  /**
   * The size of the visible avatars.
   *
   * @default 28
   */
  avatarSize?: 24 | 28
  others: OthersEvent["others"]
  self?: User
  /**
   * Whether to show hidden users as a dropdown or tooltip.
   *
   * @default "dropdown"
   */
  showHiddenUsersAs?: "dropdown" | "tooltip"
  // TODO: add avatar onClick handler, to navigate user to another's presence location
  // [PBD-2235](https://linear.app/prismic/issue/PBD-2235/allow-presence-indicators-to-navigate-users-to-other-users)
}

export function AvatarStack(props: AvatarStackProps) {
  const {
    overlapMargin = "-6",
    maximumAmountOfShownAvatars = 4,
    avatarSize = 28,
    others,
    self,
    showHiddenUsersAs = "dropdown",
  } = props

  const users = [self, ...others].filter(isDefined)

  const hiddenUsers = users.slice(maximumAmountOfShownAvatars)

  const hasHiddenUsers = users.length > maximumAmountOfShownAvatars

  return (
    <Box
      alignItems="center"
      // follow button height to align with the document header buttons
      height={theme.size.large}
    >
      <AnimatedList
        ariaLabel="Visible collaborators"
        items={users}
        getItemKey={(user) => String(user.connectionId)}
        direction="horizontal"
        animationDuration={150}
        // TODO: consider using a negative gap instead of margin on the individual items
        // [PBD-2243](https://linear.app/prismic/issue/PBD-2243/allow-negative-gap-margins-in-animatedlist)
      >
        {(user, index) => {
          if (index === maximumAmountOfShownAvatars && hasHiddenUsers) {
            return (
              <DropdownOrTooltip
                showHiddenUsersAs={showHiddenUsersAs}
                hiddenUsers={hiddenUsers}
                self={self}
                key={user.connectionId}
              >
                <Box
                  margin={{
                    left: overlapMargin,
                  }}
                >
                  <Avatar
                    fallback={`+${users.length - maximumAmountOfShownAvatars}`}
                    size={avatarSize}
                    border="grey2"
                  />
                </Box>
              </DropdownOrTooltip>
            )
          }

          return (
            <Box
              key={user.connectionId}
              margin={{
                left: index === 0 ? 0 : overlapMargin,
              }}
            >
              <Tooltip
                content={getTooltipContent({
                  user,
                  isSelf: self?.id === user?.id,
                })}
                variant="custom"
                sideOffset={7}
                stableMount
              >
                <Avatar
                  firstName={user.info?.firstname}
                  lastName={user.info?.lastname}
                  key={user.connectionId}
                  imageUrl={user.info?.avatar}
                  size={avatarSize}
                  border={user.info?.themeColor}
                />
              </Tooltip>
            </Box>
          )
        }}
      </AnimatedList>
    </Box>
  )
}

interface GetTooltipContentArgs {
  user: User
  isSelf: boolean
}

function getTooltipContent(args: GetTooltipContentArgs) {
  const { user, isSelf } = args
  const userName = getAuthorDisplayName({
    id: user.id ?? "",
    firstname: user.info?.firstname,
    lastname: user.info?.lastname,
    email: user.info?.email ?? "",
    uploadedAvatar: user.info?.avatar,
  })

  return (
    <Box gap={2}>
      <Text variant="smallBold" color="white">
        {isSelf ? "You" : userName}
      </Text>
      {isSelf && (
        <Text variant="small" color="white">
          ({userName})
        </Text>
      )}
    </Box>
  )
}

function DropdownOrTooltip(props: {
  showHiddenUsersAs: "dropdown" | "tooltip"
  self?: User
  hiddenUsers: User[]
  children: ReactNode
}) {
  const { showHiddenUsersAs, self, hiddenUsers, children } = props
  if (showHiddenUsersAs === "tooltip") {
    return (
      <Tooltip
        content={
          <Box flexDirection="column" gap={1}>
            {hiddenUsers.map((user) => (
              <Box key={user.connectionId}>
                {getTooltipContent({
                  user,
                  isSelf: self?.id === user?.id,
                })}
              </Box>
            ))}
          </Box>
        }
        variant="custom"
        sideOffset={7}
        stableMount
      >
        {children}
      </Tooltip>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" maxHeight={256} minWidth={256}>
        <DropdownMenuLabel>
          <Text color="grey11">Users on page</Text>
        </DropdownMenuLabel>
        {hiddenUsers.map((user) => (
          <DropdownMenuItem key={user.connectionId}>
            <Box gap={6} alignItems="center">
              <Avatar
                firstName={user.info?.firstname}
                lastName={user.info?.lastname}
                imageUrl={user.info?.avatar}
                size={16}
                border={user.info?.themeColor}
              />
              <Text>
                {getAuthorDisplayName({
                  id: user.id ?? "",
                  firstname: user.info?.firstname,
                  lastname: user.info?.lastname,
                  email: user.info?.email ?? "",
                  uploadedAvatar: user.info?.avatar,
                })}
              </Text>
            </Box>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
